import api from './api'

export interface BillingSummary {
  planId: string | null
  planCode: string
  planName: string
  status: string
  priceFormatted: string
  isTrial: boolean
  daysRemaining: number | null
  trialEndsAt: string | null
  credits: {
    total: number
    used: number
    remaining: number
    percentageUsed: number
  }
  features: Record<string, unknown>
  breakdown: {
    textCreditsUsed: number
    imageCreditsUsed: number
    audioCreditsUsed: number
    textMessagesCount: number
    imageMessagesCount: number
    audioMessagesCount: number
    totalMessagesCount: number
  }
}

export interface PlanItem {
  id: string
  code: string
  name: string
  priceCents: number
  monthlyMsgLimit: number
  creditsIncluded: number
  trialDays: number
  maxStores: number
  isDefault: boolean
  isActive: boolean
  features: {
    badge?: string
    description?: string
    vision?: boolean
    handoff?: boolean
    catalogLimit?: number
    webchat?: boolean
    receiptAnalysis?: boolean
    multiAgent?: boolean
    customApi?: boolean
    contactSales?: boolean
    launchOffer?: {
      regularPriceCents: number
      discountPercent: number
      months: number
    }
    [key: string]: unknown
  }
}

export async function fetchBillingSummary(): Promise<BillingSummary> {
  const { data } = await api.get<BillingSummary>('/plans/billing/summary')
  return data
}

export async function fetchAvailablePlans(): Promise<PlanItem[]> {
  const { data } = await api.get<PlanItem[]>('/plans')
  return data
}

export async function selectPlan(planCode: string): Promise<{ message: string; plan: PlanItem }> {
  const { data } = await api.post<{ message: string; plan: PlanItem }>('/plans/billing/select-plan', { planCode })
  return data
}

export async function fetchAdminPlans(): Promise<PlanItem[]> {
  const { data } = await api.get<PlanItem[]>('/plans/admin/all')
  return data
}

export async function updatePlanAdmin(id: string, updates: Partial<PlanItem>): Promise<PlanItem> {
  const { data } = await api.patch<PlanItem>(`/plans/admin/${id}`, updates)
  return data
}

export async function grantCreditsAdmin(tenantId: string, amount: number, reason: string): Promise<void> {
  await api.post('/plans/admin/grant-credits', { tenantId, amount, reason })
}

export interface PagoMovilConfig {
  bankName: string
  bankCode: string
  phoneNumber: string
  idNumber: string
  accountHolder: string
  bcvRate: number
  bcvLastUpdated: string
}

export interface PaymentReportItem {
  id: string
  tenantId: string
  planId: string
  amountUsd: number
  amountBs: number
  exchangeRate: number
  referenceNumber: string
  issuingBank?: string | null
  receiptUrl: string
  status: 'pending' | 'approved' | 'rejected'
  rejectionReason?: string | null
  notes?: string | null
  paymentDate: string
  createdAt: string
  reviewedBy?: string | null
  reviewedAt?: string | null
  plan?: PlanItem
  tenant?: { id: string; name: string; slug: string }
}

export async function fetchPagoMovilInfo(): Promise<PagoMovilConfig> {
  const { data } = await api.get<PagoMovilConfig>('/plans/billing/pago-movil-info')
  return data
}

export async function fetchBcvRate(): Promise<{ rate: number; currency: string; source: string; lastUpdated: string }> {
  const { data } = await api.get('/plans/billing/bcv-rate')
  return data
}

export async function submitPaymentReport(formData: FormData): Promise<{ message: string; report: PaymentReportItem }> {
  const { data } = await api.post<{ message: string; report: PaymentReportItem }>(
    '/plans/billing/report-payment',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return data
}

export async function fetchMyPaymentReports(): Promise<PaymentReportItem[]> {
  const { data } = await api.get<PaymentReportItem[]>('/plans/billing/my-reports')
  return data
}

export async function fetchAdminPaymentReports(status?: string): Promise<PaymentReportItem[]> {
  const { data } = await api.get<PaymentReportItem[]>('/plans/admin/payment-reports', {
    params: status && status !== 'all' ? { status } : {},
  })
  return data
}

export async function approvePaymentReportAdmin(id: string): Promise<{ message: string; report: PaymentReportItem }> {
  const { data } = await api.post<{ message: string; report: PaymentReportItem }>(`/plans/admin/payment-reports/${id}/approve`)
  return data
}

export async function rejectPaymentReportAdmin(id: string, reason: string): Promise<{ message: string; report: PaymentReportItem }> {
  const { data } = await api.post<{ message: string; report: PaymentReportItem }>(`/plans/admin/payment-reports/${id}/reject`, { reason })
  return data
}

export const billingApi = {
  fetchBillingSummary,
  fetchAvailablePlans,
  selectPlan,
  fetchAdminPlans,
  updatePlanAdmin,
  grantCreditsAdmin,
  getBillingSummary: fetchBillingSummary,
  fetchPagoMovilInfo,
  fetchBcvRate,
  submitPaymentReport,
  fetchMyPaymentReports,
  fetchAdminPaymentReports,
  approvePaymentReportAdmin,
  rejectPaymentReportAdmin,
}


